import React from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { roundMoney } from './budgetEngineV2';
import { formatCurrency, formatPercent } from '../../app/currency';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

function SummaryGroupTable({ translate, titleUpper, totalAmount, rows, formatMoney }) {
  const n = rows.length;
  if (n === 0) return null;

  const totalCellBg = (t) =>
    t.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(25, 118, 210, 0.08)';

  return (
    <Box>
      <TableContainer
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Table
          size="small"
          padding="checkbox"
          sx={{
            '& .MuiTableCell-root': {
              py: 0.55,
              px: 0.85,
              fontSize: '0.78rem',
              borderColor: 'divider',
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell
                colSpan={4}
                sx={{
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.55,
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                  }}
                >
                  {titleUpper}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const zebraBg = (t) =>
                i % 2 === 1
                  ? t.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.045)'
                    : 'rgba(0,0,0,0.028)'
                  : 'transparent';
              return (
                <TableRow key={row.key}>
                  <TableCell sx={{ color: 'text.primary', width: '38%', bgcolor: zebraBg }}>
                    {row.tooltip ? (
                      <Tooltip title={row.tooltip} arrow placement="top">
                        <Typography
                          component="span"
                          variant="inherit"
                          sx={{
                            fontSize: 'inherit',
                            minWidth: 0,
                            cursor: 'help',
                          }}
                          aria-label={translate('price_simulate.summary_row_info_aria')}
                        >
                          {row.label}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography component="span" variant="inherit" sx={{ fontSize: 'inherit', minWidth: 0 }}>
                        {row.label}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '20%',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      bgcolor: zebraBg,
                    }}
                  >
                    {row.qty}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '26%',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      bgcolor: zebraBg,
                    }}
                  >
                    {formatMoney(row.value)}
                  </TableCell>
                  {i === 0 ? (
                    <TableCell
                      rowSpan={n}
                      align="center"
                      sx={{
                        width: '16%',
                        minWidth: '5.5rem',
                        verticalAlign: 'middle',
                        borderLeft: 1,
                        borderColor: 'divider',
                        bgcolor: totalCellBg,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.15 }}>
                        {translate('price_simulate.summary_table_total')}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color="primary"
                        sx={{ mt: 0.35, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatMoney(totalAmount)}
                      </Typography>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function PriceSummaryTables({ translate, result, payloadProduct = {} }) {
  const q = Math.max(1, ni(result.detail?.quantity));
  const formatMoney = (v) => formatCurrency(v);
  const productInput = payloadProduct || {};

  const costRows = [];
  costRows.push({
    key: 'fil',
    label: translate('price_simulate.cost_filament'),
    qty: result.detail?.grams != null ? `${nf(result.detail.grams)} g` : '—',
    value: result.costs.filament,
    tooltip: translate('price_simulate.explain_filament'),
  });
  costRows.push({
    key: 'mach',
    label: translate('price_simulate.cost_printing_machine'),
    qty: result.detail?.print_hours != null ? `${nf(result.detail.print_hours)} h` : '—',
    value: result.costs.printing_machine,
    tooltip: translate('price_simulate.explain_print_machine'),
  });
  (result.costs.variable_costs || []).forEach((line, idx) => {
    costRows.push({
      key: `vc-${idx}`,
      label: line.name || '—',
      qty: `${q} un.`,
      value: line.cost_total,
      tooltip: translate('price_simulate.explain_variable_direct'),
    });
  });
  costRows.push({
    key: 'fail',
    label: translate('price_simulate.cost_failure'),
    qty:
      result.detail?.failure_percent != null ? `${formatPercent(result.detail.failure_percent)}%` : '—',
    value: result.costs.failure_cost,
    tooltip: translate('price_simulate.explain_failure'),
  });

  const serviceRows = [];
  serviceRows.push({
    key: 'mod',
    label: translate('price_simulate.revenue_modeling'),
    qty: `${nf(productInput.modeling_hours)} h`,
    value: result.services_billing.modeling,
    tooltip: translate('price_simulate.explain_service_line'),
  });
  serviceRows.push({
    key: 'print',
    label: translate('price_simulate.summary_row_print_short'),
    qty: `${nf(productInput.print_hours)} h`,
    value: result.services_billing.print_monitoring,
    tooltip: translate('price_simulate.explain_service_line'),
  });
  serviceRows.push({
    key: 'paint',
    label: translate('price_simulate.revenue_painting'),
    qty: `${nf(productInput.painting_hours)} h`,
    value: result.services_billing.painting,
    tooltip: translate('price_simulate.explain_service_line'),
  });

  const servicesTotal = roundMoney(nf(result.services_billing.total));

  const marketingMoney = Math.max(
    0,
    roundMoney(nf(result.fees.price_after_marketing) - nf(result.composition.subtotal_before_marketing)),
  );

  const hasSellingPrice = nf(result.pricing.selling_price) > 0;
  const minimumSellingPrice = nf(result.pricing.minimum_selling_price ?? result.pricing.final_price);
  const effectiveSellingPrice = nf(result.pricing.effective_selling_price ?? result.pricing.final_price);
  const minimumUnitPrice = nf(result.pricing.minimum_unit_price) || (q > 0 ? minimumSellingPrice / q : minimumSellingPrice);
  const effectiveUnitPrice = nf(result.pricing.unit_price) || (q > 0 ? effectiveSellingPrice / q : effectiveSellingPrice);

  const profitRowLabel = hasSellingPrice
    ? translate('price_simulate.implied_profit')
    : translate('price_simulate.summary_fee_profit');
  const profitRowTooltip = hasSellingPrice
    ? translate('price_simulate.explain_implied_profit')
    : translate('price_simulate.explain_profit_margin');

  const feeRows = [];
  feeRows.push({
    key: 'mkt',
    label: translate('price_simulate.summary_fee_marketing'),
    qty: `${formatPercent(result.fees.marketing_percent)}%`,
    value: marketingMoney,
    tooltip: translate('price_simulate.explain_marketing'),
  });
  feeRows.push({
    key: 'reinv',
    label: translate('price_simulate.summary_fee_reinvest'),
    qty: `${formatPercent(result.reinvestment.percent)}%`,
    value: result.reinvestment.amount,
    tooltip: translate('price_simulate.explain_reinvestment'),
  });
  feeRows.push({
    key: 'prof',
    label: profitRowLabel,
    qty: `${formatPercent(result.profit_margin.percent)}%`,
    value: result.profit_margin.amount,
    tooltip: profitRowTooltip,
  });

  const feesTotal = roundMoney(
    nf(marketingMoney) + nf(result.reinvestment.amount) + nf(result.profit_margin.amount),
  );

  const costsAndServicesTotal = result.composition.subtotal_before_marketing;

  return (
    <Stack spacing={3}>
      <SummaryGroupTable
        translate={translate}
        titleUpper={translate('price_simulate.summary_section_costs_upper')}
        totalAmount={result.costs.total_direct_cost}
        rows={costRows}
        formatMoney={formatMoney}
      />
      <SummaryGroupTable
        translate={translate}
        titleUpper={translate('price_simulate.summary_section_services_upper')}
        totalAmount={servicesTotal}
        rows={serviceRows}
        formatMoney={formatMoney}
      />
      <SummaryGroupTable
        translate={translate}
        titleUpper={translate('price_simulate.summary_section_price_buildup')}
        totalAmount={feesTotal}
        rows={feeRows}
        formatMoney={formatMoney}
      />
      <Box sx={{ mt: 0.5, pt: 1.15, borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {translate('price_simulate.summary_footer_cost')}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
              {formatMoney(costsAndServicesTotal)}
            </Typography>
          </Box>
          <Stack spacing={0.75} sx={{ textAlign: 'right' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {translate('price_simulate.minimum_selling_price')}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                {formatMoney(minimumSellingPrice)}
              </Typography>
              {q > 1 ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {translate('price_simulate.minimum_unit_price')}: {formatMoney(minimumUnitPrice)}
                </Typography>
              ) : null}
            </Box>
            {hasSellingPrice ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {translate('price_simulate.selling_price')}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="primary"
                  sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
                >
                  {formatMoney(effectiveSellingPrice)}
                </Typography>
                {q > 1 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {translate('price_simulate.selling_price_unit')}: {formatMoney(effectiveUnitPrice)}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
